import {useMemo, useRef} from 'react';
import {MediaDetection, Rect} from '@legit-health/ui';
import Konva from 'konva';
import {KonvaEventObject} from 'konva/lib/Node';
import {Vector2d} from 'konva/lib/types';
import {Layer, Stage} from 'react-konva';
import DetectionRect from './components/DetectionRect';
import Media from './components/Media';

export default function DetectionCanvas({
  scaleFactor,
  image,
  detections,
  isAddingNewDetection,
  onAddDetection,
  onDeleteDetection,
  onDragDetectionEnd,
  selectedDetection,
  onSelectDetection,
  onTransformDetection
}: {
  scaleFactor: number;
  isAddingNewDetection: boolean;
  detections: MediaDetection[];
  image: HTMLImageElement;
  onAddDetection: (pointerPosition: Vector2d) => void;
  onDeleteDetection: (detection: MediaDetection) => void;
  onDragDetectionEnd: (detection: MediaDetection, rect: Rect) => void;
  selectedDetection: MediaDetection | null;
  onSelectDetection: (detection: MediaDetection | null) => void;
  onTransformDetection: (detection: MediaDetection, rect: Rect) => void;
}) {
  const imageRef = useRef<Konva.Image>(null);
  const stageDimensions = useMemo(
    () => ({width: image.width * scaleFactor, height: image.height * scaleFactor}),
    [scaleFactor, image]
  );

  function handlePointerDown(event: KonvaEventObject<MouseEvent | TouchEvent>) {
    const clickedOnEmpty = event.target === imageRef.current;
    if (clickedOnEmpty) {
      onSelectDetection(null);
    }
    if (isAddingNewDetection) {
      const stage = event.target.getStage();
      if (!stage) {
        return;
      }
      const point = stage.getPointerPosition();
      if (!point) {
        return;
      }
      onAddDetection({
        x: point.x / scaleFactor,
        y: point.y / scaleFactor
      });
    }
  }

  return (
    <Stage
      width={stageDimensions.width}
      height={stageDimensions.height}
      onMouseDown={handlePointerDown}
      onTouchStart={handlePointerDown}
    >
      <Media image={image} stageDimensions={stageDimensions} ref={imageRef} />
      <Layer>
        {detections.map((detection) => (
          <DetectionRect
            onDelete={onDeleteDetection}
            onSelect={onSelectDetection}
            isSelected={selectedDetection?.id === detection.id}
            key={detection.id}
            detection={detection}
            scaleFactor={scaleFactor}
            onDragDetectionEnd={onDragDetectionEnd}
            onTransformDetection={onTransformDetection}
          />
        ))}
      </Layer>
    </Stage>
  );
}
